import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Link2, Loader2, CheckCircle2, X, RefreshCw, FileText, Unlink, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import IntakeQuoteSelect from "../components/intake/IntakeQuoteSelect";
import { fmtUSD } from "../components/intake/quoteMapping";

function confidenceBadge(score) {
  if (score >= 90) return { label: "Strong match", cls: "bg-green-100 text-green-800" };
  if (score >= 80) return { label: "Likely match", cls: "bg-blue-100 text-blue-800" };
  if (score >= 50) return { label: "Possible match", cls: "bg-amber-100 text-amber-800" };
  return { label: "Weak match", cls: "bg-gray-100 text-gray-700" };
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch (_) { return ""; }
}

export default function QuoteLinkManager() {
  const [data, setData] = useState({ programs: [], quote_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [includeLinked, setIncludeLinked] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [pickerFor, setPickerFor] = useState(null); // program object when the "change quote" dialog is open

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get("/quote-links/suggestions", { params: { include_linked: includeLinked } });
      setData(res.data || { programs: [], quote_count: 0 });
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load suggestions");
    } finally {
      setLoading(false);
    }
  }, [includeLinked]);

  useEffect(() => { load(); }, [load]);

  async function linkQuote(program, quote) {
    setBusyId(program.program_id);
    try {
      await apiClient.post(`/quote-links/${program.program_id}`, { quote_id: quote.id });
      toast.success("Quote linked", { description: `${program.program_name} → ${quote.client_name}` });
      setPickerFor(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to link quote");
    } finally {
      setBusyId(null);
    }
  }

  async function unlinkQuote(program) {
    setBusyId(program.program_id);
    try {
      await apiClient.delete(`/quote-links/${program.program_id}`);
      toast.success("Link removed", { description: program.program_name });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove link");
    } finally {
      setBusyId(null);
    }
  }

  const programs = data.programs || [];

  return (
    <div className="max-w-5xl mx-auto pb-20 md:pb-0">
      <div className="flex items-start justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-lg sm:text-2xl md:text-3xl font-light tracking-tight flex items-center gap-2" style={{ fontFamily: 'var(--font-sans)' }}>
            <Link2 className="w-6 h-6" /> Quote Links
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Connect activated projects back to the Client-Calculator quote they came from. We suggest the most likely match — you approve, change, or remove it.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="flex-shrink-0">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Switch id="include-linked" checked={includeLinked} onCheckedChange={setIncludeLinked} />
          <Label htmlFor="include-linked" className="text-sm cursor-pointer">Show already-linked projects</Label>
        </div>
        <span className="text-xs text-muted-foreground">{data.quote_count} saved quotes</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : error ? (
        <div className="text-sm text-red-500 py-10 text-center">{error}</div>
      ) : programs.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground text-sm">
          {includeLinked ? "No projects found." : "Every project is already linked to a quote. 🎉"}
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {programs.map((p) => {
            const top = p.suggestions?.[0];
            const busy = busyId === p.program_id;
            return (
              <Card key={p.program_id} data-testid={`program-row-${p.program_id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="text-base truncate">{p.program_name}</CardTitle>
                      <CardDescription className="text-xs mt-0.5">
                        {p.client_name}{p.client_email ? ` · ${p.client_email}` : ""}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {p.stage && <Badge variant="secondary" className="text-[10px]">{p.stage}</Badge>}
                      {p.contract_value ? <span className="text-sm font-semibold">{fmtUSD(p.contract_value)}</span> : null}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {p.source_quote_id && p.linked_quote ? (
                    // Already linked
                    <div className="flex items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5">
                      <div className="flex items-center gap-2 min-w-0 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className="text-muted-foreground">Linked to</span>
                        <span className="font-medium truncate">{p.linked_quote.client_name}</span>
                        <span className="text-muted-foreground whitespace-nowrap">{fmtUSD(p.linked_quote.total)}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button variant="outline" size="sm" className="h-8 text-xs" disabled={busy} onClick={() => setPickerFor(p)}>Change</Button>
                        <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground hover:text-destructive" disabled={busy} onClick={() => unlinkQuote(p)}>
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Unlink className="w-3.5 h-3.5 mr-1" />Remove</>}
                        </Button>
                      </div>
                    </div>
                  ) : top ? (
                    // Suggested match
                    <div className="rounded-lg border px-3 py-2.5">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0 text-sm">
                          <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                          <span className="font-medium truncate">{top.quote.client_name}</span>
                          {top.quote.client_entity && <span className="text-muted-foreground truncate hidden sm:inline">· {top.quote.client_entity}</span>}
                          <span className="text-muted-foreground whitespace-nowrap">{fmtUSD(top.quote.total)}</span>
                        </div>
                        <Badge className={`text-[10px] ${confidenceBadge(top.confidence).cls}`}>{confidenceBadge(top.confidence).label}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {top.reason} · quote by {top.quote.created_by_name || "—"} · {fmtDate(top.quote.updated_at)}
                      </p>
                      <div className="flex items-center gap-2 mt-2.5">
                        <Button size="sm" className="h-8 text-xs" disabled={busy} onClick={() => linkQuote(p, top.quote)} data-testid={`approve-${p.program_id}`}>
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                          Approve link
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 text-xs" disabled={busy} onClick={() => setPickerFor(p)}>
                          <Search className="w-3.5 h-3.5 mr-1" /> Change quote…
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // No suggestion
                    <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed px-3 py-2.5">
                      <span className="text-sm text-muted-foreground">No matching quote found.</span>
                      <Button variant="outline" size="sm" className="h-8 text-xs" disabled={busy} onClick={() => setPickerFor(p)}>
                        <Search className="w-3.5 h-3.5 mr-1" /> Find quote…
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Change / find quote picker */}
      <Dialog open={!!pickerFor} onOpenChange={(open) => { if (!open) setPickerFor(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Link a quote</DialogTitle>
            <DialogDescription>
              {pickerFor ? `Choose the quote for "${pickerFor.program_name}".` : ""}
            </DialogDescription>
          </DialogHeader>
          {pickerFor && (
            <IntakeQuoteSelect
              compact
              loadingSelection={busyId === pickerFor.program_id}
              selectingId={busyId === pickerFor.program_id ? pickerFor.source_quote_id : null}
              onSelect={(quote) => linkQuote(pickerFor, quote)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
